// @flow
import moment from 'moment';
// eslint-disable-next-line max-len
import { formatDateDisplayLocal } from '@fortress-technology-solutions/fortress-component-library/utils';
import {
  hasApplicationDateRangeNotOpened,
  isWithinApplicationDateRange,
} from '../../../utils/affordable';
import type {
  ApplicationDateRange,
  ApplicationWindow,
} from '../../../utils/affordable';
import { CONVENTIONAL_PROGRAM_NAME, MODAL_MODES } from '../constants';
import { isValidDate } from './validation';
import messages from '../messages';
import { toDateInTz } from '../../ViewProperty/AffordableSetup/WaitlistV2/utils';

type ProgramRow = { name: string, waitlistRequired: boolean };

type ProgramSummary = {
  programName: string,
  eligible: ?boolean,
  incomeLevel: ?string,
};

type ScoredProgramRow = {
  ...ProgramRow,
  eligible: ?boolean,
  incomeLevel: ?string,
};

type WaitlistConfiguration = { enabled?: boolean, programs?: Array<string> };

// Conventional units are read off the property class, the same reading
// LookingFor.hook.js uses for its unit-type options.
export const hasConventionalUnits = (propertyClassName: ?string): boolean =>
  ['Mixed', 'Conventional'].includes(propertyClassName ?? '');

// A property can carry affordable programs without running a waitlist, so the
// column is driven by the waitlist configuration rather than by the property
// programs: nothing is required while the waitlist is off, and only the
// programs the configuration enables are marked while it is on.
export const getWaitlistProgramNames = (
  waitlistConfiguration: ?WaitlistConfiguration,
): Array<string> =>
  waitlistConfiguration?.enabled ? waitlistConfiguration.programs ?? [] : [];

export const getProgramRows = ({
  propertyAffordablePrograms,
  waitlistConfiguration,
  propertyClassName,
}: {
  propertyAffordablePrograms: ?$ReadOnlyArray<?string>,
  waitlistConfiguration: ?WaitlistConfiguration,
  propertyClassName: ?string,
}): Array<ProgramRow> => {
  const waitlistPrograms = new Set(
    getWaitlistProgramNames(waitlistConfiguration),
  );
  const affordableNames = Array.from(
    new Set((propertyAffordablePrograms ?? []).filter(Boolean)),
  ).sort();

  const rows = affordableNames.map((name) => ({
    name,
    waitlistRequired: waitlistPrograms.has(name),
  }));

  return hasConventionalUnits(propertyClassName)
    ? [...rows, { name: CONVENTIONAL_PROGRAM_NAME, waitlistRequired: false }]
    : rows;
};

// How the household scores under each program the property has. A row with no
// summary — the synthetic Conventional row, and every row until the query
// answers — is unscored rather than failing.
export const mergeProgramSummaries = (
  rows: Array<ProgramRow>,
  programs: ?Array<ProgramSummary>,
): Array<ScoredProgramRow> => {
  const summariesByName = new Map(
    (programs ?? []).map((summary) => [summary.programName, summary]),
  );

  return rows.map((row) => ({
    ...row,
    eligible: summariesByName.get(row.name)?.eligible ?? null,
    incomeLevel: summariesByName.get(row.name)?.incomeLevel ?? null,
  }));
};

// Only Waitlist Programs answer to the Application Date; every other program
// keeps its own eligibility rules whatever the date.
export const markProgramAvailability = <T: { waitlistRequired: boolean }>(
  rows: Array<T>,
  {
    range,
    applicationDate,
    isRestricted,
    enforceStart = true,
  }: {
    range: ?ApplicationDateRange,
    applicationDate: ?(Date | string),
    isRestricted: boolean,
    enforceStart?: boolean,
  },
): Array<{ ...T, available: boolean }> => {
  const acceptsDate =
    !isRestricted ||
    isWithinApplicationDateRange(range, applicationDate, { enforceStart });

  return rows.map((row) => ({
    ...row,
    available: !row.waitlistRequired || acceptsDate,
  }));
};

// The range's dates and the close date are the property's local midnight as an
// instant, so the day is read in the property's timezone, as property setup
// shows it: the agent's own zone names the day before anywhere west of the
// property. Without one, UTC still names the right day for every US zone.
export const formatApplicationDate = (
  value: ?string,
  timezone?: ?string,
): string =>
  value
    ? formatDateDisplayLocal(
        moment(toDateInTz(value, timezone || 'UTC'), 'YYYY-MM-DD'),
        'MM/DD/YYYY',
      )
    : '';

const isReadableDate = (value: mixed): boolean =>
  typeof value === 'string' && moment(value).isValid();

// The day the waitlist closed. A scheduled close date that is still ahead has
// not closed anything: the waitlist was closed by hand before it, and the day
// its status changed is the honest one to name.
export const getWaitlistClosedDate = (
  configuration: ?{
    scheduledCloseDate?: ?string,
    statusEffectiveDate?: ?string,
  },
  now: Date = new Date(),
): ?string => {
  const scheduled = configuration?.scheduledCloseDate;

  if (isReadableDate(scheduled) && moment(scheduled).toDate() <= now) {
    return scheduled;
  }

  const changed = configuration?.statusEffectiveDate;

  return isReadableDate(changed) ? changed : null;
};

// The picker hands back a moment, the record an ISO string; both are compared
// as one instant. An empty or half-typed date is no date at all.
export const toApplicationDate = (value: mixed): ?Date =>
  isValidDate(value) ? moment(value).toDate() : null;

// Mirrors the backend's refusal: a closed waitlist with both dates names them,
// an open one runs from its open date (if any) to today.
export const getApplicationDateRangeText = (
  intl: Object,
  { state, range }: ApplicationWindow,
  timezone?: ?string,
): string => {
  if (!range || hasApplicationDateRangeNotOpened(range)) {
    return intl.formatMessage(messages.applicationDateRangeNone);
  }

  if (!range.start) {
    return intl.formatMessage(messages.applicationDateRangeUntilToday);
  }

  const start = formatApplicationDate(range.start, timezone);

  return state === 'PERMISSION_GATED'
    ? intl.formatMessage(messages.applicationDateRangeBetween, {
        start,
        end: formatApplicationDate(range.end, timezone),
      })
    : intl.formatMessage(messages.applicationDateRangeFrom, { start });
};

// The open date bounds a household's first entry only. Add is refused for a
// household already on the waitlist unless its row is a legacy V1 one, and
// only such a row comes back with a date, so a stored date on add marks one.
// Reinstate keeps its stored date free of the bound, but a date the agent
// moves it to is a new entry date and answers to it.
export const enforcesApplicationDateStart = ({
  mode,
  storedApplicationDate,
  applicationDate,
}: {
  mode: string,
  storedApplicationDate: ?Date,
  applicationDate: Date,
}): boolean => {
  if (mode === MODAL_MODES.ADD) {
    return storedApplicationDate == null;
  }

  if (mode === MODAL_MODES.REINSTATE) {
    return applicationDate.getTime() !== storedApplicationDate?.getTime();
  }

  return true;
};

// Edit reopens a record that has already been through the rule, so it stays
// unrestricted while its date is the stored one. Once the agent moves the
// date, the date is a new entry date and answers to the rule in full, as a
// reinstate does. A configuration with no policy gives no range to judge by,
// so nothing is withheld and the backend's own check on submit stands alone.
// Before the record loads there is no stored date to compare with, so nothing
// counts as moved.
export const isApplicationDateRestricted = ({
  mode,
  hasPolicy,
  storedApplicationDate,
  applicationDate,
}: {
  mode: string,
  hasPolicy: boolean,
  storedApplicationDate: ?Date,
  applicationDate: Date,
}): boolean => {
  if (!hasPolicy) {
    return false;
  }

  if (mode === MODAL_MODES.EDIT) {
    return (
      storedApplicationDate != null &&
      applicationDate.getTime() !== storedApplicationDate.getTime()
    );
  }

  return true;
};
